import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import CloseXSVG from '@/images/home/CloseX.svg?react'

import Portal from '@/components/common/Portal'
import useBodyScrollLock from '@/hooks/useBodyScrollLock'
import TranslationNS from '@/utils/Constants/TranslationNS'

/*
 * Organizer-mode dialog shell (mock event.html `.dlg` / `.dlg-box` / `.dlg-head` /
 * `.dlg-body`): scrim with a 4px blur, a 10px-radius card with a #f7f7f9 title band
 * and a 24px body. Centered at every width (William, Sept 30 2026: never a bottom
 * sheet) — 16px inset below 600px, the card bounded to the screen and scrolling inside.
 * Escape closes the top-most dialog only: the key is handled on the
 * dialog itself, which holds focus while open, rather than on `document`.
 *
 * Layering (z-index): album editor 250 < image viewer (antd Modal, 1000) < upload
 * 1100 < confirm 1200 — see EVENT_HUB_DIALOG_Z.
 */
export const EVENT_HUB_DIALOG_Z = {
    ALBUM: 'z-[250]',
    UPLOAD: 'z-[1100]',
    CONFIRM: 'z-[1200]',
}

/** 44px dialog action buttons (mock `.dlg-acts .btn`): one solid-ink primary per dialog. */
export const EVENT_HUB_DIALOG_BUTTON_CLASS =
    's-focus-ring flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-[5px] border px-4 text-sm font-medium transition-colors disabled:cursor-default disabled:opacity-45'
export const EVENT_HUB_DIALOG_PRIMARY_CLASS = 'border-[#272727] bg-[#272727] text-white hover:opacity-90'
export const EVENT_HUB_DIALOG_GHOST_CLASS = 'border-[#dbdadf] bg-white text-[#272727] hover:bg-[#f7f7f9]'
export const EVENT_HUB_DIALOG_DANGER_CLASS = 'border-[#b93a2c] bg-[#b93a2c] text-white hover:brightness-[.92]'

const WIDTH_CLASS = {
    sm: 'min-[601px]:w-[min(480px,100%)]',
    md: 'min-[601px]:w-[min(560px,100%)]',
    lg: 'min-[601px]:w-[min(640px,100%)]',
    // Create-event modal (mock _kit/create.css `.cev`: 600px card).
    xl: 'min-[601px]:w-[min(600px,100%)]',
    // react-pdf viewer (contract preview): a page-wide card
    pdf: 'min-[601px]:w-[min(960px,100%)]',
}

// Close button: 36px (mock `.dlg-x`) or the 44px touch target of `.cev-x`.
const CLOSE_SIZE_CLASS = {
    md: 'size-9',
    lg: 'size-11',
}

export default function EventHubDialog({
    id,
    title,
    zClass = EVENT_HUB_DIALOG_Z.UPLOAD,
    width = 'md',
    closable = true,
    closeSize = 'md',
    // Padding / gap of the scrolling body; the default is the 24px `.dlg-body`.
    bodyClassName = 'gap-4 p-6',
    // Optional fixed zone under the scrolling body (mock `.cev-acts`): it carries its own padding.
    footer,
    // Element focused when the dialog opens; falls back to the card itself.
    initialFocusRef,
    onClose,
    children,
}) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const boxRef = useRef(null)

    // Counted lock shared with the drawers and panels (hooks/bodyScrollLock): nested overlays may
    // unmount in the same commit, in any order, and the body is freed once the last one is gone.
    useBodyScrollLock()

    useEffect(() => {
        const target = initialFocusRef?.current || boxRef.current
        target?.focus({ preventScroll: true })
    }, [initialFocusRef])

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.stopPropagation()
            onClose()
        }
    }

    return (
        <Portal id={id}>
            <div
                className={`fixed inset-0 ${zClass} flex items-center justify-center p-4 min-[601px]:p-5`}
                onKeyDown={handleKeyDown}
            >
                <button
                    type="button"
                    aria-label={t('Close')}
                    tabIndex={-1}
                    // Mock `--ovl-bg`: ink at 28% behind a 4px blur.
                    className="absolute inset-0 cursor-default border-0 bg-[#0e1017]/28 p-0 backdrop-blur-[4px]"
                    onClick={onClose}
                />

                <div
                    ref={boxRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={title}
                    tabIndex={-1}
                    className={`relative z-10 flex max-h-full w-full flex-col overflow-hidden rounded-[10px] border border-[#dbdadf] bg-white shadow-[0_10px_30px_-18px_rgba(14,16,23,0.35)] outline-none ${
                        WIDTH_CLASS[width] || WIDTH_CLASS.md
                    }`}
                >
                    <header className="relative flex min-h-[51px] shrink-0 items-center justify-center border-b border-[#dbdadf] bg-[#f7f7f9] px-12 py-2.5">
                        <h2 className="font-display m-0 text-center text-[20px] font-bold leading-tight text-[#272727] [overflow-wrap:anywhere]">
                            {title}
                        </h2>
                        {closable && (
                            <button
                                type="button"
                                onClick={onClose}
                                aria-label={t('Close')}
                                className={`s-focus-ring absolute right-2 top-1/2 flex ${
                                    CLOSE_SIZE_CLASS[closeSize] || CLOSE_SIZE_CLASS.md
                                } -translate-y-1/2 cursor-pointer items-center justify-center rounded-[5px] border-0 bg-transparent text-[#272727]/60 hover:bg-white hover:text-[#272727]`}
                            >
                                <CloseXSVG className="size-4" />
                            </button>
                        )}
                    </header>

                    <div className={`flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin ${bodyClassName}`}>
                        {children}
                    </div>
                    {footer && <div className="shrink-0 border-t border-[#dbdadf]">{footer}</div>}
                </div>
            </div>
        </Portal>
    )
}

/** Two-up action row (mock `.dlg-acts`): stacks to one column below 600px. */
export function EventHubDialogActions({ children, single = false }) {
    return (
        <div className={`grid gap-3 ${single ? 'grid-cols-1' : 'max-[600px]:grid-cols-1 min-[601px]:grid-cols-2'}`}>
            {children}
        </div>
    )
}
